// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref, shallowRef, watch } from "vue";

// TMI - content type and query definitions

import type { CourseApiResponse } from "@tmi/types/api/content-models";
import { Unauthorized } from "@tmi/types/api/responses";
import type { Course, Lesson, Section } from "@tmi/types/entities";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

import { useAuthStore } from "@/stores/Auth";
import { useProductsStore } from "@/stores/Products";
import { fetchCoursesCatalog } from "@/services/data/courses-catalog";
import { mapCourseApiResponseToCourseCatalog } from "@/services/data/courses-mapper";
import { clientStateManager } from "@/services/error-services";
import { isAbortError } from "@/utils/network-utils";
import { retryOnceOnServiceUnavailable } from "@/utils/service-unavailable-retry";

// tmi-ck2.44: courses are read live from GET content/courses-catalog. Before its first cache fill the server answers
// 503 + Retry-After: that is retried once, after the Retry-After (default 5 s, at most 10 s; the shared
// retryOnceOnServiceUnavailable); a 200 with no courses is a real empty catalog.
export type CoursesLoadState = 'idle' | 'loading' | 'loaded' | 'error';

// State values
const rawCourses = shallowRef< CourseApiResponse[] >( [] );
const loadState = ref< CoursesLoadState >( 'idle' );
const loadError = shallowRef< unknown >();
// Mapped on read, not on load: the mapper filters by the member's products and permissions, which are reactive, so a
// catalog that lands before they do re-maps by itself once they arrive instead of latching an empty list.
const allCourses = computed< Course[] >( () => mapCourseApiResponseToCourseCatalog( rawCourses.value ) );
// "The server answered", not "there are rows": an empty catalog is an answer too.
const initialized = computed< boolean >( () => 'loaded' === loadState.value );

// The load in flight, joined by every concurrent caller. `generation` moves on a reset (sign-out, member switch), so a
// load that started before it drops its result; `controller` aborts its request.
let inFlight: Promise< void > | null = null;
let generation = 0;
let controller: AbortController | undefined;

const selectedCourse = computed< Course | undefined >( () => {
    const contextBasedSelectedCourse = allCourses.value.find( ( course: Course ): boolean =>
        selectedCourseSlug.value === course.slug
    );

    if ( contextBasedSelectedCourse ) {
        return contextBasedSelectedCourse;
    }
} );

const selectedCourseSlug = ref< string | undefined >();

/** The selected course's lessons in outline order, section by section: what Previous / Next and "Up next" walk. */
const courseLessons = computed< Lesson[] >( () =>
    selectedCourse.value?.sections.flatMap( ( section: Section ) => section.lessons ) ?? []
);

// UX roadmap #97: the lesson is kept by slug and resolved against the selected course, so a ?lesson= deep link read
// before the courses load still resolves once they do, and a slug from another course resolves to nothing.
const selectedLessonSlug = ref< string | undefined >();
const selectedLesson = computed< Lesson | undefined >( () => {
    const slug = selectedLessonSlug.value;
    return slug ? courseLessons.value.find( ( lesson: Lesson ): boolean => slug === lesson.slug ) : undefined;
} );

// WHY: The previous setSelectedCourseByProductSlug ran exactly once at component
// setup. If `allCourses` had not finished loading by then (which is the normal
// case — courses load via appStore.initializeAsync inside the wrapper's onMounted,
// AFTER setup), the lookup found nothing and never re-ran. CourseOverview's
// `v-if="selectedCourse"` therefore stayed false forever and the page rendered
// blank below the tab bar. We now remember the requested productSlug and resolve
// it reactively whenever `allCourses` becomes populated.
const pendingProductSlug = ref< string | undefined >();

export const useCoursesStore = defineStore( 'courses', () => {
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );

    // Before the first initializeAsync() a signed-in member is about to load, so that renders as loading, not as empty.
    const isLoading = computed< boolean >( () =>
        'loading' === loadState.value || ( 'idle' === loadState.value && !!isAuthenticated.value ) );

    function setSelectCourseByCourseSlug( courseSlug: string ) {
        selectedCourseSlug.value = courseSlug;
    }

    function tryResolvePendingProductSlug() {
        if ( !pendingProductSlug.value || allCourses.value.length === 0 ) return;

        const product = useProductsStore().productBySlug( pendingProductSlug.value );
        if ( !product ) return;

        const contextBasedSelectedCourse = allCourses.value.find( ( course: Course ): boolean =>
            product.id === course.assoc_product_ids[ 0 ]
        );

        if ( contextBasedSelectedCourse ) {
            selectedCourseSlug.value = contextBasedSelectedCourse.slug;
            pendingProductSlug.value = undefined;
        }
    }

    function setSelectedCourseByProductSlug( productSlug: string ) {
        pendingProductSlug.value = productSlug;
        // Try immediately — works fast-path when courses are already loaded.
        tryResolvePendingProductSlug();
    }

    // Reactively retry the lookup once courses populate.
    watch( allCourses, () => tryResolvePendingProductSlug(), { deep: false } );

    function reset(): void {
        generation++;
        controller?.abort();
        controller = undefined;
        inFlight = null;
        rawCourses.value = [];
        loadState.value = 'idle';
        loadError.value = undefined;
        selectedCourseSlug.value = undefined;
        selectedLessonSlug.value = undefined;
        // A product tab the previous member asked for must not resolve for the next one.
        pendingProductSlug.value = undefined;
    }

    async function load( token: string ): Promise< void > {
        const gen: number = generation;
        const abort = new AbortController();
        controller = abort;
        loadState.value = 'loading';
        loadError.value = undefined;

        try {
            // The server has not filled its cache yet: one retry after the delay it asked for, then a real failure.
            const courses: CourseApiResponse[] = await retryOnceOnServiceUnavailable(
                () => fetchCoursesCatalog( token, abort.signal ),
                { signal: abort.signal, shouldRetry: () => gen === generation } );

            if ( gen !== generation ) return;
            rawCourses.value = courses;
            loadState.value = 'loaded';
            logger.info( `[ Courses ] loaded ${ courses.length } courses` );
        } catch ( err: unknown ) {
            // A sign-out overtook this load; the next member's state is not this load's to touch.
            if ( gen !== generation ) return;
            if ( isAbortError( err ) ) {
                loadState.value = 'idle';
                return;
            }

            loadState.value = 'error';
            loadError.value = err;
            // The logger is silent in production. An expired session is already reported by goFetch.
            if ( !( err instanceof Unauthorized ) ) {
                clientStateManager.queueClientError( err, { catalog: 'courses' } );
            }
        } finally {
            if ( controller === abort ) {
                controller = undefined;
            }
        }
    }

    /**
     * Loads the member's courses once. Concurrent callers (the route's beforeEnter and the app boot) join the load in
     * flight. A failure is not latched: the next call, or retry(), loads again.
     */
    function initializeAsync(): Promise< void > {
        if ( !isAuthenticated.value || !authToken.value ) {
            logger.info( '[ Courses ].initializeAsync() exiting - not authenticated' );
            return Promise.resolve();
        }

        if ( 'loaded' === loadState.value ) {
            return Promise.resolve();
        }

        if ( inFlight ) {
            return inFlight;
        }

        const run: Promise< void > = load( authToken.value ).finally( () => {
            if ( inFlight === run ) {
                inFlight = null;
            }
        } );
        inFlight = run;
        return run;
    }

    /** The courses views' "Try again". */
    function retry(): Promise< void > {
        return initializeAsync();
    }

    /** Selects a lesson of the selected course; undefined (or a slug the course does not have) selects none. */
    function selectLessonBySlug( slug: string | undefined ): void {
        selectedLessonSlug.value = slug || undefined;
    }

    return {
        // ACTIONS
        initializeAsync,
        reset,
        retry,
        selectLessonBySlug,
        setSelectCourseByCourseSlug,
        setSelectedCourseByProductSlug,

        // GETTERS
        allCourses,
        courseLessons,
        initialized,
        isLoading,
        loadError,
        loadState,
        selectedCourse,
        selectedLesson
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useCoursesStore, import.meta.hot ) );
}
